import { RankingCompetition } from './ranking-models';

/**
 * Server-provided ranking filter option (backend `/ranking/option` and
 * `/group/{id}/ranking/option`). The selected option is sent back verbatim as the body
 * of the ranking request (`POST /ranking`, `POST /group/{id}/ranking`).
 *
 * The backend resolves a ranking by the EXACT option it handed out — every field, not
 * just some of them. Any deviation — a made-up title such as `'Saison'` instead of the
 * server's `'Saison 2026/2027'`, the right title with the wrong `orderIndex`, or a
 * dropped `subtitle` — answers 404 (`errorCode: 20002`). There is therefore no such
 * thing as a client-side default option: only an option that came from the server may
 * ever be sent, and it must go back unchanged.
 */
export interface RankingFilterOption {
  /** `null` = an overall/period ranking; otherwise the backend competition enum value. */
  competition: RankingCompetition | null;
  title: string;
  /**
   * Optional second line under the title, e.g. `'14. Spieltag'`. `null` when the server
   * sent none — which today is always the case on backends that do not know the field
   * yet. Must be sent back verbatim: the backend matches the option on all fields, so a
   * dropped subtitle turns into a 404.
   */
  subtitle: string | null;
  orderIndex: number;
}

/**
 * The option that should be preselected once the server-defined options arrive: the one
 * with the lowest `orderIndex`. Returns `null` when the server sent no options at all
 * (e.g. the season has not started yet) — callers must then skip the ranking request
 * entirely rather than invent an option the backend would reject.
 */
export function firstRankingFilterOption(options: readonly RankingFilterOption[]): RankingFilterOption | null {
  if (!options.length) return null;
  return options.reduce((lowest, option) => (option.orderIndex < lowest.orderIndex ? option : lowest));
}

/**
 * A ranking filter option as the competition filter modal renders it: the server's title
 * plus the icon the frontend picks for the competition. Pure data — it lives here rather
 * than next to the modal so `type:util` code can build it without depending on `type:ui`.
 */
export interface CompetitionFilterOption {
  /** `null` = an overall/period ranking; otherwise the backend competition enum value. */
  value: RankingCompetition | null;
  /** Ready-made title delivered by the backend — rendered as-is, never translated. */
  label: string;
  /** Frontend-picked icon; omitted for icon-less lists (e.g. months). */
  icon?: string;
  /** Second line under the label (server-provided, e.g. `'14. Spieltag'`); `null` = none. */
  subtitle?: string | null;
}
